import formatNumber from './formatNumber';
import isNumber from './isNumber';

/**
 * Formats a number as a monetary value.
 *
 * @param value - The value to format. If the value is not a number, it will be returned as is.
 * @param rounded - If true, rounds the value to the nearest integer. If 'thousands', rounds the value to the nearest (lower) thousand.
 * @param isCentUnit - If true, the value is assumed to be in cents and will be divided by 100. Defaults to true.
 * @param showCurrency - If true, appends the currency symbol 'kr' to the formatted value. Defaults to true.
 * @returns The formatted monetary value as a string, or the original value if it is not a number.
 */
export function formatMoney(
  value: number,
  rounded?: boolean | 'thousands',
  isCentUnit?: boolean,
  showCurrency?: boolean,
): string;
export function formatMoney(
  value: unknown,
  rounded?: boolean | 'thousands',
  isCentUnit?: boolean,
  showCurrency?: boolean,
): undefined;
export function formatMoney(
  value: string | unknown,
  rounded?: boolean | 'thousands',
  isCentUnit = true,
  showCurrency = true,
): string | unknown {
  if (!isNumber(value)) {
    return value;
  }

  const rounding = rounded ?? false;

  let convertedValue = isCentUnit ? value / 100 : value;
  if (rounding === 'thousands') {
    convertedValue = Math.floor(convertedValue / 1000);
    if (convertedValue < 1) {
      convertedValue = 0;
    }
  }

  return `${formatNumber(convertedValue, {
    minimumFractionDigits: rounding === false ? 2 : 0,
    maximumFractionDigits: rounding === false ? 2 : 0,
    useGrouping: rounding === 'thousands' ? false : undefined,
  }).replace(
    /\D00$/,
    '',
  )}${rounding === 'thousands' && convertedValue >= 1 ? "'" : ''}${showCurrency ? ' kr' : ''}`;
}

function roundLargeValue(value: number) {
  // Only round values above 10k
  if (value < 10_000) {
    return value;
  }

  // Round to nearest 10k on values below 1 million
  if (value < 1_000_000) {
    return Math.round(value / 10_000) * 10_000;
  }

  // Round to nearest 100k on values up to 10 million
  if (value <= 10_000_000) {
    return Math.round(value / 100_000) * 100_000;
  }

  // Round to nearest 500k on values up to 20 million
  if (value <= 20_000_000) {
    return Math.round(value / 500_000) * 500_000;
  }

  // Round to nearest 1 million
  return Math.round(value / 1_000_000) * 1_000_000;
}

export function formatLargeMoneyValue(
  value: number,
  options?: { showCurrency?: boolean; shortenMillionSuffix?: boolean },
): string;
export function formatLargeMoneyValue<T>(
  value: T,
  options?: { showCurrency?: boolean; shortenMillionSuffix?: boolean },
): T;
export function formatLargeMoneyValue(
  value: number | unknown,
  { showCurrency = true, shortenMillionSuffix = false } = {},
): string | unknown {
  if (!isNumber(value)) {
    return value;
  }

  const ONE_MILLION = 1_000_000;

  const roundedAmount = roundLargeValue(value);

  if (roundedAmount < ONE_MILLION) {
    return formatMoney(roundedAmount, false, false, showCurrency);
  }

  const [millions, thousands] = (roundedAmount / ONE_MILLION)
    .toString()
    .split('.');

  const strAmount = thousands != null ? `${millions},${thousands}` : millions;

  const millionSuffix = shortenMillionSuffix
    ? 'milj'
    : roundedAmount === ONE_MILLION
      ? 'miljon'
      : 'miljoner';

  return `${strAmount} ${millionSuffix}${showCurrency ? ' kr' : ''}`;
}
